import * as React from "react";
import { ChevronRight } from "lucide-react";

import { Button } from "../button";
import { Card } from "../card";
import { cn } from "../utils";

/** Section card — promoted from the `/styleguide` workflow prototypes
 *  (`app/styleguide/_card-strip.tsx`); the workflow detail page's one card
 *  shape. Frame is `px-6 py-4`. `bleed` (default) pulls the card `-mx-6` past
 *  the text rail so its px-6 padding lands the title back ON the rail — every
 *  card frame then lines up on both edges. Grid cells pass `bleed={false}`
 *  (the grid bleeds, not the cell). */
export function SectionCard({
  title,
  aside,
  action,
  description,
  children,
  className,
  bleed = true,
}: {
  title: string;
  aside?: React.ReactNode;
  action?: React.ReactNode;
  /** One-line description under the title. It rides tight to the title (the
   *  house masthead spacing, `gap-1`) instead of landing a full `gap-4` below
   *  it as a first child would — a subtitle reads as part of the heading, not as
   *  the section's opening row of content. */
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bleed?: boolean;
}) {
  // Static-height title row so a card WITH an action button reads the same as
  // one without — content then starts at the same offset every card.
  const header = (
    <div className="flex h-7 items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <h2 className="type-title tracking-normal text-foreground">{title}</h2>
        {aside}
      </div>
      {action}
    </div>
  );
  return (
    <Card className={cn("flex h-full flex-col gap-4 px-6 py-4", bleed && "-mx-6", className)}>
      {description ? (
        <div className="flex flex-col gap-1">
          {header}
          <p className="max-w-2xl type-body text-muted-foreground">{description}</p>
        </div>
      ) : (
        header
      )}
      {children}
    </Card>
  );
}

/** The card-header nav action ("All runs ›", "Open pipeline ›") — the
 *  `ghost-muted`/`nav` Button; `-mr-2` (layout only) restores the card's
 *  optical right gutter. */
export function SectionNav({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <Button variant="ghost-muted" size="nav" className="-mr-2" onClick={onClick}>
      {label}
      <ChevronRight />
    </Button>
  );
}
